import { useEffect, useState } from "react";
import { useApolloClient } from "@apollo/client/react";
import { Loader2, Shield } from "lucide-react";
import { Card } from "@/components/ui/card";
import { startImpersonationSession, type ImpersonationSession } from "@/lib/session-utils";

function parseImpersonationPayload(): ImpersonationSession | null {
  const hash = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;
  const payload = new URLSearchParams(hash).get("payload");
  if (!payload) {
    return null;
  }

  try {
    return JSON.parse(payload) as ImpersonationSession;
  } catch (error) {
    console.error("Failed to parse impersonation payload:", error);
    return null;
  }
}

export default function Impersonate() {
  const apolloClient = useApolloClient();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const bootstrapImpersonation = async () => {
      const session = parseImpersonationPayload();
      if (!session?.accessToken || !session?.refreshToken || !session?.user?.email || !session?.organization?.id) {
        setError("Missing impersonation session details.");
        return;
      }

      startImpersonationSession(session);
      apolloClient.stop();
      await apolloClient.clearStore();
      window.location.replace("/");
    };

    bootstrapImpersonation().catch((err) => {
      console.error("Failed to start impersonation session:", err);
      setError("Failed to start impersonation session.");
    });
  }, [apolloClient]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-6">
      <Card className="w-full max-w-md p-8 border-border shadow-xl">
        <div className="flex flex-col items-center text-center gap-4">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
            {error ? <Shield className="w-7 h-7 text-destructive" /> : <Loader2 className="w-7 h-7 text-primary animate-spin" />}
          </div>
          <div className="space-y-1">
            <h1 className="text-xl font-semibold">{error ? "Unable to impersonate user" : "Starting impersonation"}</h1>
            <p className="text-sm text-muted-foreground">
              {error || "Setting up a tab-scoped dashboard session for the selected user."}
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
